import { View as BackboneView } from 'backbone'
import isFunction from 'lodash/isFunction'
import isArray from 'lodash/isArray'
import flatten from 'lodash/flatten'
import extend from 'lodash/extend'
import pick from 'lodash/pick'
import union from 'lodash/union'
import { getUrlParameters, to, getPathname } from 'helpers/url-helpers'
import { locationAssign } from 'helpers/redirect-helper'
import $ from 'init/jquery'
import { isMobile } from 'init/is-mobile'

/**
 * Базовая вью приложения
 */
export const View = BackboneView.extend({
  existParamsDefault: ['currency', 'mode'],
  existParamsCurrencyLink: ['mode', 'enable_wholesale_price'],
  existParamsSortLink: ['currency', 'mode'],
  existParamsReturnUrl: ['f', 'c_id'],
  attachedOptions: ['cart', 'settlement', 'user', 'template'],
  additionalOptions: [],

  /**
   * Выполняется перед инициализацией.
   * @param {Object} data Данные, переданные в конструктор.
   * @abstract
   */
  beforeRun (data) {},

  /**
   * Инициализирует представление.
   * @param {Object} data Данные, переданные в конструктор.
   * @abstract
   */
  run (data) {},

  /**
   * Выполняется после инициализации.
   * @param {Object} data Данные, переданные в конструктор.
   * @abstract
   */
  afterRun (data) {},

  /** @inheritdoc */
  initialize (data) {
    this.childViews = {}
    this.attachOptions(data)
    this.beforeRun(data)
    this.run(data)
    this.afterRun(data)
  },

  /**
   * Обновляет содержимое элемента при наличии шаблона.
   * @param {Object} [data] Объект с данными для передачи в шаблон.
   * @abstract
   */
  render (data) {
    data = data || {}
    if (isFunction(this.template)) {
      this.$el.html(this.template(data))
    }
  },

  /**
   * Сохраняем переданные опции,
   * если они входят в массив attachedOptions
   * @param {Object} options Опции
   */
  attachOptions (options) {
    if (!options) {
      return
    }
    let optionList = union(this.attachedOptions, this.additionalOptions)
    optionList.forEach(function (optionName) {
      if (options.hasOwnProperty(optionName) && typeof options[optionName] !== 'undefined') {
        this[optionName] = options[optionName]
      }
    }.bind(this))
  },

  /**
   * Показывает/скрывает элемент вью за указанное время
   * @param {number} [time] Время за которое показывается/скрывается элемент вью
   */
  toggle (time) {
    time = isNaN(time)
      ? 500
      : Number(time)
    this.$el.stop(true, true).animate({
      height: 'toggle',
      opacity: 'toggle',
    }, time)
  },

  /**
   * Удаляет вид и все дочерние виды.
   */
  remove () {
    if (this.childViews) {
      this._removeChildViews()
    }
    BackboneView.prototype.remove.apply(this, arguments)
  },

  /**
   * Рекурсивно удаляет дочерние виды.
   * @protected
   */
  _removeChildViews () {
    for (const view in this.childViews) {
      if (this.childViews.hasOwnProperty(view)) {
        if (isArray(this.childViews[view])) {
          // Если массив, убираем вложенность и применяем remove() ко всем элементам
          flatten(this.childViews[view]).forEach(function (item) {
            if (isFunction(item.remove)) {
              item.remove()
            }
          })
        } else {
          this.childViews[view].remove()
        }
      }
    }
  },

  /**
   * Добавляет параметры из текущего URL к ссылке
   * @param {jQuery|HTMLElement} $el Элемент для обработки
   * @param {boolean} isCurrencyLink Является ли ссылка переключателем валюты
   * @param {boolean} isSortLink Является ли ссылка переключателем сортировки
   * @param {Object} additionalParams Дополнительные get-параметры ссылки
   */
  _addExistParams ($el, isCurrencyLink, isSortLink, additionalParams) {
    let url = $el.attr('href'),
      hash
    if (url) {
      [url, hash] = url.split('#')
      const path = url.split('?')[0],
        params = getUrlParameters(url)
      let existParams = this.existParamsDefault
      if (additionalParams) {
        extend(params, additionalParams)
      }
      // для ссылок изменения валюты не перезаписываем параметр, относящийся к валюте
      if (isCurrencyLink) {
        existParams = this.existParamsCurrencyLink
      }
      if (isSortLink) {
        existParams = this.existParamsSortLink
      }

      if (params.hasOwnProperty('returnUrl')) {
        existParams = this.existParamsReturnUrl
        const currentUrlParams = getUrlParameters()
        const existParamsObj = pick(currentUrlParams, existParams)
        if (Object.keys(existParamsObj).length) {
          const newReturnUrl = to(getPathname(), {}, existParamsObj)
          params['returnUrl'] = btoa(encodeURIComponent(newReturnUrl))
        }
      }

      for (let i = 0; i < existParams.length; i += 1) {
        if (params.hasOwnProperty(existParams[i]) && params[existParams[i]] !== undefined) {
          delete params[existParams[i]]
        }
      }
      url = to(path, existParams, params)
      if (!path) {
        url = url.replace(/^\//, '')
      }
      if (hash) {
        url += '#' + hash
      }
      $el.attr('href', url)
    }
  },

  /**
   * Редиректит на страницу авторизации  с параметром returnUrl.
   */
  redirectToAuth () {
    locationAssign(to(
      '/auth/',
      {},
      { returnUrl: window.location.pathname })
    )
  },

  /**
   * Обрабатывает взаимодействие со ссылками на странице
   * @param {Object} [additionalParams] Дополнительные параметры урла
   */
  rewritePageLinks (additionalParams = null) {
    const eventType = isMobile ? 'click' : 'mouseover'
    const pageLinks = this.$('a')
    this.linksAdditionalParams = additionalParams
    pageLinks.off(`${eventType}.rewriteLinks`)
    pageLinks.on(`${eventType}.rewriteLinks`, this.eventHandlerPageLinks.bind(this))
  },

  /**
   * Навешивает событие по которому необходимо перезаписать ссылку
   * @param {Event} e - событие
   */
  eventHandlerPageLinks (e) {
    const target = e.currentTarget,
      $target = this.$(target)
    if (isMobile) {
      if (!$target.attr('href')
        || $target.hasClass('js-no-rewrite-link')
        || $target.attr('target') === '_blank'
      ) {
        return
      }
      e.preventDefault()
      this.rewriteElementLink(target, this.linksAdditionalParams)
      locationAssign($target.attr('href'))
    } else {
      this.rewriteElementLink(target, this.linksAdditionalParams)
    }
  },

  /**
   * Перезаписывает ссылку переданного элемента с сохранением параметров каталога
   * @param {Object} el
   * @param {Object} additionalParams
   */
  rewriteElementLink (el, additionalParams) {
    const $el = $(el),
      isCurrencyLink = $el.hasClass('choice-currency'),
      isSortLink = !!$el.data('sort'),
      isAuthLink = $el.hasClass('auth-link'),
      isWithoutRedirect = $el.attr('href') === '#',
      isHashTagSearch = $el.hasClass('js-hash-tag-search'),
      isNoRewriteLink = $el.hasClass('js-no-rewrite-link')

    if (el.pathname) {
      if (
        (el.pathname.indexOf('.') !== -1 && el.pathname.indexOf('index-test') === -1)
        || el.hostname !== location.hostname
        || el.pathname.indexOf('partner/wiki') !== -1
        || isAuthLink
        || isWithoutRedirect
        || isNoRewriteLink
        || isHashTagSearch
      ) {
        return
      }
      this._addExistParams($el, isCurrencyLink, isSortLink, additionalParams)
    }
  },
  /**
   * Добавляем нужный гет-параметр к элементам.
   * @param {string} selector
   * @param {Object} params
   */
  addUrlParamsToLinks (selector, params) {
    $(selector).each((index, el) => {
      this.rewriteElementLink(el, params)
    })
  },
})
